import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { Button } from '@/components/ui/button.js';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import { apiFetch } from '@/lib/api/client.js';
import { toast } from '@/lib/toast.js';
import { importOrder } from '@/features/orders/api.js';
import { sendInvoiceByIdOrNumber } from '@/features/pennylane/api.js';

interface CatchupJob { id: string; status: string; counts: { found: number; launched: number; ignored: number; errors: number; dateOnly?: number } | null; error: string | null }

export function CatchupSection({ source, store }: { source: 'shopify' | 'pennylane'; store?: string }): JSX.Element {
  const [reference, setReference] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const job = useQuery({
    queryKey: ['catchup', jobId],
    queryFn: () => apiFetch<CatchupJob>(`/api/catchup/${jobId}`),
    enabled: jobId !== null,
    refetchInterval: (query) => ['pending', 'running'].includes(query.state.data?.status ?? '') ? 2000 : false,
  });
  const byId = useMutation({
    mutationFn: async () => {
      if (source === 'shopify') {
        if (!store) throw new Error('Boutique manquante.');
        const result = await importOrder({ store, reference: reference.trim() });
        return result.created ? 'Commande ajoutée à la préparation.' : 'Commande déjà connue : aucun nouvel envoi.';
      }
      const result = await sendInvoiceByIdOrNumber(reference.trim(), false);
      return `Facture ${result.invoiceNumber} mise en préparation.`;
    },
    onSuccess: (message) => { toast.success(message); setReference(''); },
    onError: (error) => { toast.fromError(error); },
  });
  const byPeriod = useMutation({
    mutationFn: () => apiFetch<{ jobId: string }>('/api/catchup', {
      method: 'POST', body: { source, ...(store ? { store } : {}), from, to, confirmed: true },
    }),
    onSuccess: (result) => { setJobId(result.jobId); setConfirm(false); toast.success('Rattrapage lancé en arrière-plan.'); },
    onError: (error) => { setConfirm(false); toast.fromError(error); },
  });
  return (
    <PageSection title="Récupérer des commandes passées" description="Les éléments déjà transmis sont ignorés. Le renvoi d’un élément transmis se fait depuis sa fiche, avec confirmation.">
      <div className="space-y-5">
        <div className="flex flex-wrap items-end gap-3">
          <Field label={source === 'shopify' ? 'ID ou numéro de commande' : 'ID ou numéro de facture'} htmlFor={`catchup-id-${source}`}>
            <Input id={`catchup-id-${source}`} value={reference} onChange={(event) => setReference(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (reference.trim() && !byId.isPending) byId.mutate();
                }
              }} />
          </Field>
          <Button type="button" variant="outline" disabled={!reference.trim()} isLoading={byId.isPending} onClick={() => byId.mutate()}>Récupérer</Button>
        </div>
        <div className="flex flex-wrap items-end gap-3 border-t border-border pt-5">
          <Field label="Début (heure de Paris)" htmlFor={`catchup-from-${source}`}>
            <Input id={`catchup-from-${source}`} type="datetime-local" value={from} onChange={(event) => setFrom(event.target.value)} />
          </Field>
          <Field label="Fin (heure de Paris)" htmlFor={`catchup-to-${source}`}>
            <Input id={`catchup-to-${source}`} type="datetime-local" value={to} onChange={(event) => setTo(event.target.value)} />
          </Field>
          <Button type="button" disabled={!from || !to || from > to} onClick={() => setConfirm(true)}>Récupérer la période</Button>
        </div>
        {job.data ? <p className="text-sm" role="status">
          {job.data.status === 'done' ? 'Terminé' : job.data.status === 'failed' ? 'En erreur' : 'En cours'}
          {job.data.counts ? ` · ${job.data.counts.found} trouvés · ${job.data.counts.launched} lancés · ${job.data.counts.ignored} ignorés · ${job.data.counts.errors} en erreur` : ''}
          {job.data.error ? ` · ${job.data.error}` : ''}
        </p> : null}
        {source === 'pennylane' && (job.data?.counts?.dateOnly ?? 0) > 0 ? (
          <p className="text-xs text-muted-foreground">Pennylane n’a fourni qu’une date pour {job.data?.counts?.dateOnly} facture(s) des jours limites : elles ont été incluses sans filtrage à l’heure.</p>
        ) : null}
      </div>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="Récupérer toute la période ?"
        description="Toutes les commandes de la période seront examinées en arrière-plan. Les éléments déjà envoyés seront ignorés."
        confirmLabel="Lancer le rattrapage" isConfirming={byPeriod.isPending}
        onConfirm={() => byPeriod.mutate()} />
    </PageSection>
  );
}
